import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { cmsBool, previewOgValues } from "@/lib/seoHead";
import { GEO_DISCLAIMER } from "@/lib/siteContent";
import { DEFAULTS } from "@/lib/cms";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminPageHeader,
  AdminPagination,
  AdminSubTabs,
  paginateItems,
  previewText,
  useAdminQueryParam,
} from "@/pages/admin/AdminUx";
import AioDashboard from "@/pages/admin/AioDashboard";
import {
  SearchContentCms,
  TargetQueryPanel,
  EvidenceMapPanel,
  AuthorityEntityPanel,
} from "@/pages/admin/SearchAuthorityPanels";
import {
  AdminConfirmDialog,
  AdminSectionIntro,
  AdminStickySaveBar,
  AdminViewModeToggle,
  SeoAdminViewProvider,
  SeoLabeledField,
  SeoLivePreview,
  useSeoAdminView,
} from "@/pages/admin/SeoAeoGeoUx.jsx";
import {
  AEO_FIELDS,
  GEO_ENTITY_FIELDS,
  PAGE_SEO_FIELDS,
  SEO_FIELDS,
  TAB_META,
  isUnpublished,
  pickKeys,
  tabLabel,
} from "@/pages/admin/seoAeoGeoUx";

const TAB_IDS = [
  "aio", "common", "pages", "aeo", "queries", "evidence", "guides",
  "landings", "geo", "authority", "jsonld", "engines",
];

const COMMON_KEYS = [
  "site_title", "seo_description", "seo_keywords",
  "seo_og_title", "seo_og_description", "seo_og_url", "seo_canonical_url",
];
const GEO_KEYS = [
  "brand_name_en", "brand_alternate_names", "brand_summary",
  "brand_expertise", "brand_target_industries", "brand_service_region",
];
const ENGINE_KEYS = [
  "seo_google_verification", "seo_naver_verification", "seo_bing_verification",
  "seo_ga_id", "seo_robots_enabled", "seo_sitemap_enabled",
];

function useCmsData() {
  const [data, setData] = useState({});
  const [saved, setSaved] = useState({});
  const load = async () => {
    const r = await api.get("/cms");
    const next = r.data || {};
    setData(next);
    setSaved(next);
  };
  useEffect(() => { load(); }, []);
  return { data, setData, saved, setSaved, load };
}

function EntityList({ title, items, qFields, onEdit, onDelete, onAdd, renderMeta, view }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) => qFields.map((k) => String(row[k] || "")).join(" ").toLowerCase().includes(needle));
  }, [items, q, qFields]);
  const paged = paginateItems(filtered, page, 8);
  useEffect(() => setPage(1), [q]);
  return (
    <div>
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="검색" />
        <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-sm" onClick={onAdd}>+ 추가</button>
        <span className="admin-count">{filtered.length}개</span>
      </div>
      {paged.items.length === 0 ? (
        <AdminEmptyState message={`${title}이(가) 없습니다.`} />
      ) : (
        <div className="admin-row-list">
          {paged.items.map((row) => {
            const unpublished = isUnpublished(row) || row.enabled === false;
            return (
              <div key={row.id} className="admin-row">
                <div className="admin-row__main">
                  <div className="admin-row__title">{renderMeta(row).title}</div>
                  <div className="admin-row__preview">{previewText(renderMeta(row).preview)}</div>
                  <div className="admin-row__meta">
                    {row.enabled === false ? "비활성" : "활성"}
                    {unpublished ? " · 검색에 아직 반영되지 않음" : ""}
                    {view === "dev" && row.id ? ` · ${row.id}` : ""}
                  </div>
                </div>
                <div className="admin-row__actions">
                  <button type="button" className="admin-row__btn" onClick={() => onEdit(row)}>수정</button>
                  {onDelete ? <button type="button" className="admin-row__btn admin-row__btn--danger" onClick={() => onDelete(row)}>삭제</button> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <AdminPagination page={paged.page} pageSize={8} total={filtered.length} onPageChange={setPage} />
    </div>
  );
}

function SeoAeoGeoBody({ onSaved }) {
  const { view } = useSeoAdminView();
  const [tab, setTab] = useAdminQueryParam("section", "aio");
  const { data, setData, saved, setSaved, load } = useCmsData();
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [pages, setPages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [qa, setQa] = useState([]);
  const [services, setServices] = useState([]);
  const [sources, setSources] = useState([]);
  const [editing, setEditing] = useState(null);
  const [kind, setKind] = useState("");
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const tabs = TAB_IDS.map((id) => ({ id, label: tabLabel(id, view) }));
  const saveKeys = tab === "common" ? [...COMMON_KEYS, "media_og_image"]
    : tab === "geo" ? GEO_KEYS
    : tab === "engines" ? ENGINE_KEYS
    : [];
  const dirty = saveKeys.length
    ? JSON.stringify(pickKeys(data, saveKeys)) !== JSON.stringify(pickKeys(saved, saveKeys))
    : false;

  const reloadAll = async () => {
    const [p, f, q, s, src, ov] = await Promise.all([
      api.get("/admin/seo/pages"),
      api.get("/admin/aeo/faqs"),
      api.get("/admin/aeo/service-qa"),
      api.get("/admin/geo/services"),
      api.get("/admin/geo/sources"),
      api.get("/admin/seo/overview"),
    ]);
    setPages(p.data.items || []);
    setFaqs(f.data.items || []);
    setQa(q.data.items || []);
    setServices(s.data.items || []);
    setSources(src.data.items || []);
    setStats(ov.data.completeness || null);
  };

  useEffect(() => { reloadAll().catch(() => toast.error("SEO 데이터 로드 실패")); }, []);
  useEffect(() => { setJustSaved(false); }, [tab]);

  const saveCms = async (keys) => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach((k) => { payload[k] = data[k] ?? ""; });
      await api.put("/admin/cms", payload);
      toast.success("저장 완료 · DEV 홈페이지 반영됨");
      setJustSaved(true);
      setSaved((prev) => ({ ...prev, ...payload }));
      if (onSaved) onSaved();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const saveEntity = async () => {
    if (!editing || !kind) return;
    const routes = {
      page: ["/admin/seo/pages", pages],
      faq: ["/admin/aeo/faqs", faqs],
      qa: ["/admin/aeo/service-qa", qa],
      service: ["/admin/geo/services", services],
      source: ["/admin/geo/sources", sources],
    };
    const [base] = routes[kind];
    try {
      if (editing.id) await api.put(`${base}/${editing.id}`, editing);
      else await api.post(base, editing);
      toast.success("저장되었습니다.");
      setJustSaved(true);
      setEditing(null);
      await reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };

  const removeEntity = async () => {
    if (!pendingDelete) return;
    const map = {
      faq: "/admin/aeo/faqs",
      qa: "/admin/aeo/service-qa",
      service: "/admin/geo/services",
      source: "/admin/geo/sources",
    };
    try {
      await api.delete(`${map[pendingDelete.k]}/${pendingDelete.row.id}`);
      toast.success("삭제되었습니다.");
      setPendingDelete(null);
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "삭제 실패");
    }
  };

  const og = previewOgValues(data, {});
  const c = stats || {};
  const editingUnpublished = isUnpublished(editing) || editing?.enabled === false;

  return (
    <div data-testid="admin-seo">
      <AdminPageHeader
        title={view === "dev" ? "SEO · AEO · GEO · AIO" : "검색 · AI 최적화"}
        description={view === "dev"
          ? "AIO Readiness는 공개 근거 준비도입니다. Google 순위·ChatGPT 추천·AI 노출 확률을 예측하지 않습니다."
          : "검색과 AI가 DSITESOLUTION을 올바르게 이해하도록 제목, 설명, 질문, 근거를 관리합니다. 숫자는 준비도이며 순위나 추천을 보장하지 않습니다."}
        actions={<AdminViewModeToggle />}
        extra={c.page_seo ? (
          <p className="text-xs text-white/45 mt-2" data-testid="seo-completeness">
            {view === "dev"
              ? `페이지 SEO ${c.page_seo.filled}/${c.page_seo.total} · FAQ ${c.faq_count}개 · Organization ${c.organization.filled}/${c.organization.total} · Service ${c.service_entity_count}개 · JSON-LD ${c.jsonld_active ? "활성" : "없음"}`
              : `페이지 검색 ${c.page_seo.filled}/${c.page_seo.total} · FAQ ${c.faq_count}개 · 회사 정보 ${c.organization.filled}/${c.organization.total} · 서비스 ${c.service_entity_count}개 · 검색엔진용 회사 정보 ${c.jsonld_active ? "활성" : "없음"}`}
          </p>
        ) : null}
      />
      <AdminSubTabs tabs={tabs} value={tab} onChange={setTab} />
      <AdminSectionIntro tabId={tab} />
      {saveKeys.length ? (
        <AdminStickySaveBar
          dirty={dirty}
          saved={justSaved && !dirty}
          onSave={() => saveCms(saveKeys)}
          saving={saving}
        />
      ) : null}

      {tab === "aio" && <AioDashboard onSection={setTab} />}

      {tab === "common" && (
        <div className="seo-ux-layout">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {COMMON_KEYS.map((key) => (
              <SeoLabeledField key={key} meta={SEO_FIELDS[key]} fieldKey={key} view={view}>
                {key.includes("description") ? (
                  <textarea rows={3} value={data[key] || ""} onChange={(e) => { setData({ ...data, [key]: e.target.value }); setJustSaved(false); }} data-testid={`seo-${key}`} />
                ) : (
                  <input value={data[key] || ""} onChange={(e) => { setData({ ...data, [key]: e.target.value }); setJustSaved(false); }} data-testid={`seo-${key}`} />
                )}
              </SeoLabeledField>
            ))}
          </div>
          <div className="seo-ux-layout__preview">
            <SeoLivePreview
              title={data.site_title || DEFAULTS.site_title}
              url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
              description={data.seo_description || DEFAULTS.seo_description}
              ogTitle={og.ogTitle}
              ogDescription={og.ogDescription}
              ogUrl={og.ogUrl}
              ogImage={og.ogImage}
              aiItems={[
                { label: "검색 제목", value: data.site_title || DEFAULTS.site_title },
                { label: "한 줄 설명", value: data.seo_description || DEFAULTS.seo_description },
                { label: "대표 주소", value: data.seo_canonical_url || DEFAULTS.seo_canonical_url },
                { label: "키워드", value: data.seo_keywords },
              ]}
            />
          </div>
        </div>
      )}

      {tab === "pages" && (
        <EntityList
          title={TAB_META.pages[view === "dev" ? "dev" : "simple"]}
          items={pages}
          qFields={["path", "seo_title", "label"]}
          view={view}
          onAdd={() => { setKind("page"); setEditing({ path: "/", seo_title: "", meta_description: "", robots: "index,follow" }); }}
          onEdit={(row) => { setKind("page"); setEditing({ ...row }); }}
          renderMeta={(row) => ({ title: `${row.path} ${row.label || ""}`, preview: row.seo_title || row.meta_description })}
        />
      )}

      {tab === "aeo" && (
        <div className="space-y-8">
          <p className="text-xs text-white/45">
            {view === "dev"
              ? "AEO Entity는 사이트 정보(회사명·연락처·대표자)를 재사용합니다. FAQ JSON-LD는 실제로 렌더되는 /about, 가이드, 서비스 페이지에만 출력됩니다."
              : "회사 이름·연락처는 사이트 정보에서 가져옵니다. FAQ는 소개·가이드·서비스 페이지에 실제로 보일 때만 검색엔진에도 전달됩니다."}
          </p>
          <h4 className="text-sm text-white/70">{view === "dev" ? "FAQ" : "자주 묻는 질문"}</h4>
          <EntityList title="FAQ" items={faqs} qFields={["question", "category"]} view={view} onAdd={() => { setKind("faq"); setEditing({ question: "", answer: "", category: "일반", enabled: true }); }} onEdit={(row) => { setKind("faq"); setEditing({ ...row }); }} onDelete={(row) => setPendingDelete({ k: "faq", row })} renderMeta={(row) => ({ title: row.question, preview: row.answer })} />
          <h4 className="text-sm text-white/70">{view === "dev" ? "서비스 Q&A" : "서비스별 질문과 답변"}</h4>
          <EntityList title="Q&A" items={qa} qFields={["service_name", "question"]} view={view} onAdd={() => { setKind("qa"); setEditing({ service_name: "", question: "", answer: "", enabled: true }); }} onEdit={(row) => { setKind("qa"); setEditing({ ...row }); }} onDelete={(row) => setPendingDelete({ k: "qa", row })} renderMeta={(row) => ({ title: `${row.service_name || ""} ${row.question}`, preview: row.answer })} />
        </div>
      )}

      {tab === "queries" && <TargetQueryPanel />}
      {tab === "evidence" && <EvidenceMapPanel />}
      {tab === "guides" && <SearchContentCms kind="guides" />}
      {tab === "landings" && <SearchContentCms kind="services" />}
      {tab === "authority" && <AuthorityEntityPanel />}

      {tab === "geo" && (
        <div className="space-y-6">
          <p className="text-xs text-white/60 border border-white/10 rounded-xl p-4" data-testid="geo-disclaimer">{GEO_DISCLAIMER}</p>
          <div className="seo-ux-layout">
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {GEO_KEYS.map((key) => (
                <SeoLabeledField key={key} meta={SEO_FIELDS[key]} fieldKey={key} view={view}>
                  <input value={data[key] || ""} onChange={(e) => { setData({ ...data, [key]: e.target.value }); setJustSaved(false); }} />
                </SeoLabeledField>
              ))}
            </div>
            <div className="seo-ux-layout__preview">
              <SeoLivePreview
                title={data.brand_name_en || data.site_title || DEFAULTS.site_title}
                url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
                description={data.brand_summary || data.seo_description || DEFAULTS.seo_description}
                ogTitle={data.brand_name_en || og.ogTitle}
                ogDescription={data.brand_summary || og.ogDescription}
                ogUrl={og.ogUrl}
                ogImage={og.ogImage}
                aiItems={[
                  { label: "브랜드", value: data.brand_name_en },
                  { label: "다른 표기", value: data.brand_alternate_names },
                  { label: "회사 소개", value: data.brand_summary },
                  { label: "전문 분야", value: data.brand_expertise },
                  { label: "대상 산업", value: data.brand_target_industries },
                  { label: "서비스 지역", value: data.brand_service_region },
                ]}
              />
            </div>
          </div>
          <h4 className="text-sm text-white/70">{view === "dev" ? "Service Entity" : "서비스 정보"}</h4>
          <EntityList title="서비스" items={services} qFields={["name", "short_description"]} view={view} onAdd={() => { setKind("service"); setEditing({ name: "", short_description: "", detailed_description: "", enabled: true }); }} onEdit={(row) => { setKind("service"); setEditing({ ...row }); }} onDelete={(row) => setPendingDelete({ k: "service", row })} renderMeta={(row) => ({ title: row.name, preview: row.short_description })} />
          <h4 className="text-sm text-white/70">{view === "dev" ? "Official Sources" : "공식 출처"}</h4>
          <EntityList title="출처" items={sources} qFields={["label", "url"]} view={view} onAdd={() => { setKind("source"); setEditing({ label: "", url: "/", source_type: "page", enabled: true }); }} onEdit={(row) => { setKind("source"); setEditing({ ...row }); }} onDelete={(row) => setPendingDelete({ k: "source", row })} renderMeta={(row) => ({ title: row.label || row.url, preview: row.url })} />
        </div>
      )}

      {tab === "jsonld" && (
        <div className="space-y-3">
          <p className="text-xs text-white/45">
            {view === "dev"
              ? "폼 데이터로 생성합니다. raw JSON 직접 수정은 없습니다."
              : "아래 경로를 고르면 검색엔진이 읽는 회사 정보가 미리보기로 나타납니다. JSON을 직접 수정하지 않습니다."}
          </p>
          <div className="flex flex-wrap gap-2">
            {["/", "/about", "/guides", "/services/homepage", "/guides/homepage-agency-criteria", "/portfolio/brand-web"].map((path) => (
              <button key={path} type="button" className="admin-row__btn" onClick={async () => {
                const r = await api.get("/public/jsonld", { params: { path } });
                setPreview(r.data);
              }}>{path}</button>
            ))}
          </div>
          {preview ? (
            <SeoLivePreview
              title={data.site_title || DEFAULTS.site_title}
              url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
              description={data.seo_description || DEFAULTS.seo_description}
              ogTitle={og.ogTitle}
              ogDescription={og.ogDescription}
              ogUrl={og.ogUrl}
              ogImage={og.ogImage}
              aiItems={(preview.graphs || []).slice(0, 6).map((g, i) => ({
                label: view === "dev" ? (g["@type"] || `graph ${i + 1}`) : `검색엔진이 읽는 정보 ${i + 1}`,
                value: g.name || g.headline || g["@type"] || JSON.stringify(g).slice(0, 80),
              }))}
            />
          ) : null}
          <pre className="bg-black/40 border border-white/10 rounded-xl p-4 text-xs overflow-auto max-h-96" data-testid="jsonld-preview">
            {preview ? JSON.stringify({ "@context": "https://schema.org", "@graph": preview.graphs }, null, 2) : "경로를 선택하면 미리보기가 표시됩니다."}
          </pre>
        </div>
      )}

      {tab === "engines" && (
        <div className="seo-ux-layout">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {["seo_google_verification", "seo_naver_verification", "seo_bing_verification", "seo_ga_id"].map((key) => (
              <SeoLabeledField key={key} meta={SEO_FIELDS[key]} fieldKey={key} view={view}>
                <input value={data[key] || ""} onChange={(e) => { setData({ ...data, [key]: e.target.value }); setJustSaved(false); }} />
              </SeoLabeledField>
            ))}
            <SeoLabeledField meta={SEO_FIELDS.seo_robots_enabled} fieldKey="seo_robots_enabled" view={view}>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={cmsBool(data.seo_robots_enabled)} onCheckedChange={(v) => { setData({ ...data, seo_robots_enabled: v ? "true" : "false" }); setJustSaved(false); }} />
                {cmsBool(data.seo_robots_enabled) ? "사용 중" : "꺼짐"}
              </label>
            </SeoLabeledField>
            <SeoLabeledField meta={SEO_FIELDS.seo_sitemap_enabled} fieldKey="seo_sitemap_enabled" view={view}>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={cmsBool(data.seo_sitemap_enabled)} onCheckedChange={(v) => { setData({ ...data, seo_sitemap_enabled: v ? "true" : "false" }); setJustSaved(false); }} />
                {cmsBool(data.seo_sitemap_enabled) ? "사용 중" : "꺼짐"}
              </label>
            </SeoLabeledField>
            <p className="md:col-span-2 text-xs text-white/40" data-testid="seo-engines-apply-note">
              {view === "dev"
                ? "저장은 Production CMS(DB)에 즉시 기록됩니다. 검색엔진 인증 메타/GA/robots/sitemap 은 FastComet의 index.php·seo-robots.php·seo-sitemap.php가 공개 API를 읽어 최초 HTML에 주입합니다. 값이 있다고 검색엔진 인증 성공으로 표시하지 않습니다."
                : "저장하면 서버에 값이 남습니다. 검색엔진이 실제로 읽는 것은 사이트 첫 HTML과 /robots.txt, /sitemap.xml 입니다. 인증 성공 여부는 Google·Naver·Bing 콘솔에서 확인하세요."}
            </p>
          </div>
          <div className="seo-ux-layout__preview">
            <SeoLivePreview
              title={data.site_title || DEFAULTS.site_title}
              url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
              description={data.seo_description || DEFAULTS.seo_description}
              ogTitle={og.ogTitle}
              ogDescription={og.ogDescription}
              ogUrl={og.ogUrl}
              ogImage={og.ogImage}
              aiItems={[
                { label: "Google 소유 확인", value: data.seo_google_verification ? "값 저장됨" : "아직 없음" },
                { label: "Naver 소유 확인", value: data.seo_naver_verification ? "값 저장됨" : "아직 없음" },
                { label: "Bing 소유 확인", value: data.seo_bing_verification ? "값 저장됨" : "아직 없음" },
                { label: "GA 측정 ID", value: data.seo_ga_id ? "값 저장됨" : "아직 없음" },
                { label: "robots.txt", value: cmsBool(data.seo_robots_enabled) ? "사용 중(API)" : "꺼짐" },
                { label: "sitemap.xml", value: cmsBool(data.seo_sitemap_enabled) ? "사용 중(API)" : "꺼짐" },
              ]}
            />
          </div>
        </div>
      )}

      <AdminEditDrawer
        open={Boolean(editing)}
        title={view === "dev" ? "편집" : "내용 수정"}
        onClose={() => setEditing(null)}
        footer={<button type="button" className="btn-primary" onClick={saveEntity}>저장</button>}
      >
        {editing && kind === "page" ? (
          <>
            {editingUnpublished ? <p className="seo-ux-pill seo-ux-pill--draft mb-3">검색에 아직 반영되지 않음</p> : null}
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.seo_title || data.site_title}
              url={`https://dsitesolution.com${editing.path || "/"}`}
              description={editing.meta_description || data.seo_description}
              ogTitle={editing.seo_title}
              ogDescription={editing.meta_description}
              ogUrl={`https://dsitesolution.com${editing.path || "/"}`}
              aiItems={[
                { label: "페이지 주소", value: editing.path },
                { label: "검색 수집", value: editing.robots },
              ]}
            />
            <SeoLabeledField meta={PAGE_SEO_FIELDS.path} fieldKey="path" view={view}>
              <input value={editing.path || ""} onChange={(e) => setEditing({ ...editing, path: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={PAGE_SEO_FIELDS.seo_title} fieldKey="seo_title" view={view}>
              <input value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} data-testid="page-seo-title" />
            </SeoLabeledField>
            <SeoLabeledField meta={PAGE_SEO_FIELDS.meta_description} fieldKey="meta_description" view={view}>
              <textarea rows={3} value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={PAGE_SEO_FIELDS.robots} fieldKey="robots" view={view}>
              <input value={editing.robots || ""} onChange={(e) => setEditing({ ...editing, robots: e.target.value })} />
            </SeoLabeledField>
          </>
        ) : null}
        {editing && kind === "faq" ? (
          <>
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.question}
              url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
              description={editing.answer}
              ogTitle={editing.question}
              ogDescription={editing.answer}
              aiItems={[
                { label: "질문", value: editing.question },
                { label: "답변", value: editing.answer },
                { label: "분류", value: editing.category },
              ]}
            />
            <SeoLabeledField meta={AEO_FIELDS.question} fieldKey="question" view={view}>
              <input value={editing.question || ""} onChange={(e) => setEditing({ ...editing, question: e.target.value })} data-testid="faq-question" />
            </SeoLabeledField>
            <SeoLabeledField meta={AEO_FIELDS.answer} fieldKey="answer" view={view}>
              <textarea rows={4} value={editing.answer || ""} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={AEO_FIELDS.category} fieldKey="category" view={view}>
              <input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
            </SeoLabeledField>
          </>
        ) : null}
        {editing && kind === "qa" ? (
          <>
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.question}
              url={data.seo_canonical_url || DEFAULTS.seo_canonical_url}
              description={editing.answer}
              ogTitle={editing.question}
              ogDescription={editing.answer}
              aiItems={[
                { label: "서비스", value: editing.service_name },
                { label: "질문", value: editing.question },
                { label: "답변", value: editing.answer },
              ]}
            />
            <SeoLabeledField meta={AEO_FIELDS.service_name} fieldKey="service_name" view={view}>
              <input value={editing.service_name || ""} onChange={(e) => setEditing({ ...editing, service_name: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={AEO_FIELDS.question} fieldKey="question" view={view}>
              <input value={editing.question || ""} onChange={(e) => setEditing({ ...editing, question: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={AEO_FIELDS.answer} fieldKey="answer" view={view}>
              <textarea rows={4} value={editing.answer || ""} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} />
            </SeoLabeledField>
          </>
        ) : null}
        {editing && kind === "service" ? (
          <>
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.name}
              url={`https://dsitesolution.com${editing.official_page_url || ""}`}
              description={editing.short_description}
              ogTitle={editing.name}
              ogDescription={editing.short_description}
              aiItems={[
                { label: "서비스", value: editing.name },
                { label: "짧은 설명", value: editing.short_description },
                { label: "공식 주소", value: editing.official_page_url },
              ]}
            />
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.name} fieldKey="name" view={view}>
              <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.short_description} fieldKey="short_description" view={view}>
              <textarea rows={2} value={editing.short_description || ""} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.detailed_description} fieldKey="detailed_description" view={view}>
              <textarea rows={3} value={editing.detailed_description || ""} onChange={(e) => setEditing({ ...editing, detailed_description: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.official_page_url} fieldKey="official_page_url" view={view}>
              <input value={editing.official_page_url || ""} onChange={(e) => setEditing({ ...editing, official_page_url: e.target.value })} />
            </SeoLabeledField>
          </>
        ) : null}
        {editing && kind === "source" ? (
          <>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.label} fieldKey="label" view={view}>
              <input value={editing.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.url} fieldKey="url" view={view}>
              <input value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={GEO_ENTITY_FIELDS.source_type} fieldKey="source_type" view={view}>
              <input value={editing.source_type || ""} onChange={(e) => setEditing({ ...editing, source_type: e.target.value })} />
            </SeoLabeledField>
          </>
        ) : null}
      </AdminEditDrawer>

      <AdminConfirmDialog
        open={Boolean(pendingDelete)}
        title="정말 삭제할까요?"
        message="삭제하면 목록에서 사라집니다. 검색·AI 연결에도 영향을 줄 수 있습니다."
        onCancel={() => setPendingDelete(null)}
        onConfirm={removeEntity}
      />
    </div>
  );
}

export default function SeoAeoGeoTab({ onSaved }) {
  return (
    <SeoAdminViewProvider>
      <SeoAeoGeoBody onSaved={onSaved} />
    </SeoAdminViewProvider>
  );
}
